import { X, BookOpen, Upload, MousePointer2, Sparkles, Shield, Zap, Search, Layers } from 'lucide-react';

interface DocumentationProps {
    onClose: () => void;
}

export function Documentation({ onClose }: DocumentationProps) {
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-slate-900 w-full max-w-4xl max-h-[90vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-brand-100 dark:bg-brand-500/20 rounded-xl">
                            <BookOpen className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 italic tracking-tight">How to use HAR Insight</h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Master the ultimate HTTP Archive analyzer</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-12 pb-12 custom-scrollbar">
                    {/* Section 1: Getting Started */}
                    <section>
                        <div className="flex items-center gap-2 mb-4 text-brand-600 dark:text-brand-400">
                            <Upload className="w-4 h-4" />
                            <h3 className="text-sm font-bold uppercase tracking-wider">01. Getting Started</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800">
                                <h4 className="font-bold text-sm mb-2">Importing Data</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                    Drag and drop any <code className="px-1 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">.har</code> file anywhere on the dashboard. You can also click the upload area to select files from your computer.
                                </p>
                            </div>
                            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800">
                                <h4 className="font-bold text-sm mb-2">Multi-Tab Analysis</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                    Open multiple HAR files simultaneously. Use the tabs at the top to switch between capture sessions effortlessly.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Section 2: Core Components */}
                    <section>
                        <div className="flex items-center gap-2 mb-4 text-violet-600 dark:text-violet-400">
                            <Layers className="w-4 h-4" />
                            <h3 className="text-sm font-bold uppercase tracking-wider">02. The Dashboard</h3>
                        </div>
                        <div className="space-y-4">
                            <DocItem
                                icon={<Zap className="w-4 h-4" />}
                                title="Waterfall Chart"
                                description="Visualize the timing of every request. Hover over bars to see detailed phase breakdowns like DNS lookup, SSL handshake, and TTFB."
                            />
                            <DocItem
                                icon={<Search className="w-4 h-4" />}
                                title="Smart Filtering"
                                description="Search by URL, filter by status code, method, or domain. Use common patterns like 'error' or '304' to quickly find issues."
                            />
                            <DocItem
                                icon={<Sparkles className="w-4 h-4" />}
                                title="AI Performance Insights"
                                description="Get AI analysis of your HAR file. Click 'Investigate Request' to jump directly to related entries in the sidebar."
                            />
                            <DocItem
                                icon={<Shield className="w-4 h-4" />}
                                title="Security & Cache Audit"
                                description="Identify missing security headers (CSP, HSTS) and find caching optimization opportunities for static assets."
                            />
                        </div>
                    </section>

                    {/* Section 3: Pro Tips */}
                    <section>
                        <div className="flex items-center gap-2 mb-4 text-amber-600 dark:text-amber-400">
                            <MousePointer2 className="w-4 h-4" />
                            <h3 className="text-sm font-bold uppercase tracking-wider">03. Pro Tips</h3>
                        </div>
                        <ul className="space-y-3">
                            <li className="flex gap-3 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                                <span>Use <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded shadow-sm text-[10px]">J</kbd> and <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded shadow-sm text-[10px]">K</kbd> keys to navigate through requests in the table like a pro.</span>
                            </li>
                            <li className="flex gap-3 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                                <span>Need to collaborate? Use the <strong>Share</strong> button to generate a temporary link (expiring in 7 days) that includes your HAR data.</span>
                            </li>
                            <li className="flex gap-3 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                                <span>Click on any chart segment in <strong>Content Type</strong> or <strong>Third Party</strong> charts to instantly filter for those specific requests.</span>
                            </li>
                        </ul>
                    </section>
                </div>

                {/* Footer */}
                <div className="p-6 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center group">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest italic group-hover:text-brand-500 transition-colors">Analyze Fast. Secure by Default.</p>
                    <button
                        onClick={onClose}
                        className="btn-primary py-2 px-6 text-xs h-auto"
                    >
                        Got it, thanks!
                    </button>
                </div>
            </div>
        </div>
    );
}

function DocItem({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
    return (
        <div className="flex gap-4 p-4 rounded-2xl hover:bg-slate-50 dark:hover:bg-white/5 transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-800">
            <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-800 shadow-sm flex items-center justify-center text-slate-500 dark:text-slate-400 border border-slate-100 dark:border-slate-700 mt-0.5">
                {icon}
            </div>
            <div className="flex-1">
                <h4 className="font-bold text-sm text-slate-900 dark:text-slate-100 mb-1">{title}</h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{description}</p>
            </div>
        </div>
    );
}
